import { useState } from 'react';
import { ArrowRight, Check, Gift, QrCode } from 'lucide-react';
import type { FaixaPreco, Precos as PrecosApi } from '../../api/tipos';
import { BotaoLink, Esqueleto, cx } from '../../componentes/ui';
import { numero, reais } from '../../util/formato';
import { CabecalhoSecao } from './CabecalhoSecao';
import base from './publico.module.css';
import css from './Precos.module.css';

/** Quantidades do simulador. A faixa e o preço saem da tabela do servidor. */
const SIMULACOES = [500, 5_000, 50_000, 500_000];

/**
 * Preços, vindos do servidor (ver `usePrecos`).
 *
 * Falha em silêncio: se o servidor não responder, a seção some em vez de
 * mostrar um preço inventado ou um erro na cara de quem chegou pelo anúncio.
 */
export function Precos({
  precos,
  falhou,
  logado,
}: {
  precos: PrecosApi | null;
  falhou: boolean;
  logado: boolean;
}) {
  if (falhou) return null;

  const faixas = precos?.faixas ?? null;
  const bonus = precos?.bonusCadastroCentavos ?? 0;
  const menor = faixas?.reduce(
    (m, f) => Math.min(m, f.precoCentavos),
    Number.POSITIVE_INFINITY,
  );

  return (
    <section id="precos" className={base.secao}>
      <div className={base.conteudo}>
        <CabecalhoSecao sobretitulo="Preços" titulo="Você paga por XML entregue">
          Sem mensalidade, sem pacote para vencer, sem cobrança por usuário.
          Adiciona crédito por Pix e usa quando precisar — a faixa conta o
          lote somado ao que você baixou nos últimos 30 dias, então quem baixa
          muito paga menos até em buscas pequenas
          {menor !== undefined && Number.isFinite(menor) && (
            <>
              , chegando a <b>{reais(menor)} por XML</b> em volume de escritório
            </>
          )}
          .
        </CabecalhoSecao>

        {faixas === null ? (
          <Esqueleto altura={164} />
        ) : (
          <div className={css.tabela} role="list">
            {faixas.map((f) => {
              const melhor = f.precoCentavos === menor;
              return (
                <div
                  key={f.de}
                  role="listitem"
                  className={cx(css.faixa, melhor && css.faixaMelhor)}
                >
                  <span className={css.volume}>
                    {f.ate === null
                      ? `A partir de ${numero(f.de)} XMLs`
                      : `${numero(f.de)} a ${numero(f.ate)} XMLs`}
                  </span>
                  <span className={css.preco}>{reais(f.precoCentavos)}</span>
                  <span className={css.unidade}>por XML</span>
                  {melhor && <span className={css.selo}>Menor preço</span>}
                </div>
              );
            })}
          </div>
        )}

        <div className={css.inferior}>
          {faixas && faixas.length > 0 ? (
            <Simulador faixas={faixas} />
          ) : (
            <Esqueleto altura={180} />
          )}

          <ul className={css.garantias}>
            <li>
              <Check aria-hidden="true" />
              <span>
                <b>Enviar e analisar o SPED é gratuito.</b> Extrair as chaves e
                filtrar não custa nada — o crédito só é usado no download.
              </span>
            </li>
            <li>
              <Check aria-hidden="true" />
              <span>
                <b>Nota sem XML não é cobrada.</b> O valor do lote fica
                reservado e, no fim, a diferença do que não foi entregue volta
                para o seu saldo.
              </span>
            </li>
            <li>
              <Check aria-hidden="true" />
              <span>
                <b>O preço é travado no início.</b> A faixa é definida quando a
                busca começa, então o valor não muda no meio do caminho — e
                o volume dos últimos 30 dias já entra nela.
              </span>
            </li>
            <li>
              <QrCode aria-hidden="true" />
              <span>
                <b>Recarga por Pix.</b> QR Code na hora e crédito automático na
                confirmação. Sem boleto para esperar, sem cartão cadastrado.
              </span>
            </li>
          </ul>
        </div>

        {bonus > 0 && (
          <div className={css.bonus}>
            <span className={css.bonusIcone} aria-hidden="true">
              <Gift />
            </span>
            <div className={css.bonusTexto}>
              <strong>Comece com {reais(bonus)} em créditos</strong>
              <span>
                Entram na conta assim que você confirmar o e-mail. Sem cartão e
                sem compromisso — é crédito de verdade, para baixar XML de
                verdade.
              </span>
            </div>
            {!logado && (
              <BotaoLink
                to="/criar-conta"
                data-clique="criar-conta-precos"
                iconeDireita={<ArrowRight />}
              >
                Criar conta grátis
              </BotaoLink>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

/** Faixa em que um lote de `qtd` XMLs cai — a mesma regra do servidor. */
function faixaDe(faixas: FaixaPreco[], qtd: number): FaixaPreco | undefined {
  return faixas.find((f) => qtd >= f.de && (f.ate === null || qtd <= f.ate));
}

function Simulador({ faixas }: { faixas: FaixaPreco[] }) {
  const [qtd, setQtd] = useState(SIMULACOES[1]);
  const faixa = faixaDe(faixas, qtd);

  return (
    <div className={css.simulador}>
      <span className={css.simuladorTitulo}>Quanto custa um lote de</span>
      <div className={css.opcoes} role="group" aria-label="Tamanho do lote">
        {SIMULACOES.map((q) => (
          <button
            key={q}
            type="button"
            aria-pressed={q === qtd}
            className={cx(css.opcao, q === qtd && css.opcaoAtiva)}
            onClick={() => setQtd(q)}
          >
            {numero(q)}
          </button>
        ))}
      </div>
      {faixa && (
        <div className={css.resultado} aria-live="polite">
          <span className={css.total}>{reais(qtd * faixa.precoCentavos)}</span>
          <span className={css.conta}>
            {numero(qtd)} XMLs × {reais(faixa.precoCentavos)}
          </span>
        </div>
      )}
      <span className={css.simuladorNota}>
        Estimativa para lotes com todas as notas entregues. O custo exato
        aparece antes de você confirmar a busca.
      </span>
    </div>
  );
}
